import type { DesignSide } from '../store/designStore';
import type { FabricCanvas } from '../types/fabric';
import { crossOriginForUrl } from './crossOriginImage';
import { registerCurvedText } from './fabric.curvedText';

// Custom Fabric object properties preserved through toJSON() calls in this
// file — same list as historyStore.ts's HISTORY_SNAPSHOT_PROPS, kept as a
// separate literal here (not imported) because this is a plain lib file, not
// a store/hook, and importing zustand-store-adjacent modules here would be an
// odd dependency direction. If this list and historyStore.ts's ever drift,
// re-sync them by hand — see historyStore.ts's own header comment for the
// 2026-08-06 incident this list already caused once by omission.
const CANVAS_JSON_PROPS = [
  'name', 'tab', 'imageSrc', 'converted_orignal_file_url', 'whiteURL', 'originalURL', 'originalUploadId', 'originalUploadUrl', 'printColors', 'spotColorId',
];

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

// Fabric's own loadFromJSON/enlivenObjects reads crossOrigin PER OBJECT from
// each image object's own JSON data (fabric.js, fabric.Image.fromObject) —
// it is never set by default, and none of this codebase's saved canvas JSON
// ever carries it (customer-authored content has no reason to). Without it,
// an image loaded from a cross-origin host (S3/CDN — the common case for any
// customer-uploaded artwork) taints the canvas the moment it's drawn, and the
// later toDataURL() call either throws (silently swallowed by this
// function's callers' own try/catch, see designPreviewCapture.ts /
// DesignCaptureRoot.tsx) or — confirmed live 2026-08-14 — succeeds but omits
// every tainted image, rendering text/shape objects fine while every S3-
// hosted image object on the same side is simply missing. designDownload.ts
// was previously immune only by accident: the live DesignerWorkspace canvas
// this file's OTHER call sites usually snapshot from (snapshotCanvasJson's
// canvasInstances branch) already has each image's crossOrigin set correctly
// by the various <img crossOrigin={crossOriginForUrl(...)}> JSX call sites
// that originally loaded it — this function only ever hit the gap when
// rebuilding a side from COLD, either-side-cache JSON, which is every path
// through DesignCaptureRoot.tsx/DesignDownloadRoot.tsx (no live canvas to
// inherit from) and any DesignerWorkspace side not currently mounted. Same
// crossOriginForUrl() helper every other image tag in this codebase already
// uses (see that file's own header for the matching CORS-cache-race
// incident this is a sibling bug to, not a duplicate of).
function withImageCrossOrigin(canvasJson: any): any {
  if (!canvasJson || !Array.isArray(canvasJson.objects)) return canvasJson;
  return {
    ...canvasJson,
    objects: canvasJson.objects.map((obj: any) =>
      obj?.type === 'image' && typeof obj.src === 'string'
        ? { ...obj, crossOrigin: crossOriginForUrl(obj.src) ?? null }
        : obj,
    ),
  };
}

// Renders a side's saved canvas JSON into an off-screen StaticCanvas at its original,
// un-zoomed dimensions (the live <canvas> element may currently be scaled down by
// DesignerApp's viewport zoom-fit, so reading its pixels directly would produce an
// inconsistent, low-res result depending on which side happens to be active).
async function renderSideDesignDataUrl(side: DesignSide, canvasJson: any): Promise<string> {
  const { fabric } = await import('fabric');
  // useFabricCanvas.ts registers fabric.CurvedText on the LIVE editing canvas's
  // own `await import('fabric')`, gated by a module-private `fabricRegistered`
  // flag this file has no access to. This function runs its own separate
  // `import('fabric')` for every side that has no currently-mounted live
  // canvas — which is every side for the read-only preview/download routes
  // (DesignWidgetRoot's `?mode=widget` embed, ApprovalPreview, PDF/template
  // export) that never mount <DesignerWorkspace> at all, and any non-active
  // side even inside the real designer. Without registering here too,
  // fabric.CurvedText doesn't exist in this module instance, so
  // loadFromJSON silently fails to deserialize any `type: 'curved-text'`
  // object — confirmed live (design af0ed176-405f-4b79-900a-12f5f9f569db,
  // customedge.navabrindsolutions.com): the one saved side with a
  // curved-text object rendered "Voorbeeld genereren mislukt" in the
  // ?mode=widget preview while plain text/image sides on the same design
  // rendered fine. registerCurvedText() is itself idempotent (early-returns
  // if fabric.CurvedText already exists), so this is safe to call
  // unconditionally regardless of whether useFabricCanvas already did.
  registerCurvedText(fabric);
  const offscreen = new fabric.StaticCanvas(undefined, {
    width:  side.canvasWidth,
    height: side.canvasHeight,
  });
  if (canvasJson) {
    await new Promise<void>(resolve => offscreen.loadFromJSON(withImageCrossOrigin(canvasJson), () => resolve()));
  }
  offscreen.renderAll();
  const dataUrl = (offscreen as any).toDataURL({ format: 'png' });
  offscreen.dispose();
  return dataUrl;
}

/**
 * Composites a side's design onto its full product photo, at the design area's real
 * pixel position within that photo — the same `left`/`top`/`width`/`height` the admin
 * side editor saves (`DesignArea`), not the CSS-scaled position the live viewport shows.
 * Returns a PNG data URL suitable for a print-quality PDF page (full source resolution
 * unless `maxDimension` caps it — see `buildPreviewDataUrl`, which does for the on-screen
 * thumbnail: vendor source photos can be print-resolution, e.g. GivingEurope SKUs run
 * ~5600px/2.6MB, and drawing/encoding that at full size just for a ~240px tile is the
 * dominant cost in the Preview tab feeling slow to load).
 */
async function compositeSideForDownload(side: DesignSide, designDataUrl: string, variantOverlayUrl?: string, maxDimension?: number): Promise<string> {
  const [photo, design, overlay] = await Promise.all([
    loadImage(side.imagePath),
    loadImage(designDataUrl),
    variantOverlayUrl ? loadImage(variantOverlayUrl) : Promise.resolve(null),
  ]);

  const fullWidth  = photo.naturalWidth  || photo.width;
  const fullHeight = photo.naturalHeight || photo.height;
  const scale = maxDimension ? Math.min(1, maxDimension / Math.max(fullWidth, fullHeight)) : 1;

  const canvas = document.createElement('canvas');
  canvas.width  = Math.round(fullWidth  * scale);
  canvas.height = Math.round(fullHeight * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable');

  // Draw order: base photo → Object Variant overlay (the chosen mesh's
  // color/texture preview, swapping the garment/material look) → the
  // customer's Fabric design on top, which must never be obscured.
  ctx.drawImage(photo, 0, 0, canvas.width, canvas.height);
  if (overlay) ctx.drawImage(overlay, 0, 0, canvas.width, canvas.height);
  ctx.drawImage(design, side.areaLeft * scale, side.areaTop * scale, side.canvasWidth * scale, side.canvasHeight * scale);

  return canvas.toDataURL('image/png');
}

// Only the *active* side has a live Fabric canvas in `canvasInstances` —
// DesignerWorkspace reuses one <canvas> across sides, disposing the previous
// one on every switch. `sideColorCountJsonCache` (populated by
// useFabricCanvas's cleanup, keyed by sideKey THEN by print-color-count — see
// designStore.ts's field comment) is what makes every other already-edited
// side's content available here.
//
// For the mounted side, the LIVE canvas already reflects whatever count is
// currently on screen, so reading it directly is correct. For every OTHER
// side, this must resolve which count is actually active for THAT side
// (resolveActiveColorCountKey) before reading the cache — reading a flat
// per-side cache without that resolution would silently return whichever
// count happened to be cached last, not the one the customer currently has
// selected, corrupting PDF export / thumbnails / preview for any side that
// isn't the one on screen.
function snapshotCanvasJson(
  side: DesignSide,
  canvasInstances: Map<string, FabricCanvas>,
  sideColorCountJsonCache: Record<string, Record<string, any>>,
  resolveActiveColorCountKey: (side: DesignSide) => string,
): any {
  const canvas = canvasInstances.get(side.sideKey);
  if (canvas) {
    return canvas.toJSON(CANVAS_JSON_PROPS);
  }
  const countKey = resolveActiveColorCountKey(side);
  return sideColorCountJsonCache[side.sideKey]?.[countKey];
}

// Mirrors backend/src/services/printSpecs.ts's collectObjectSummaries() type
// discrimination (textbox/curved-text/text -> "text") and
// backend/src/services/psdService.ts's describeLayerName() naming
// convention exactly, so a layer named "Text: Hello" here matches what the
// server-side print-worker path would call the same kind of object.
function describeLayerName(obj: any, index: number): string {
  if (obj?.name) return obj.name;
  if (obj?.type === 'textbox' || obj?.type === 'curved-text' || obj?.type === 'text') {
    const text = (typeof obj.text === 'string' ? obj.text : '').trim();
    const snippet = text.length > 30 ? `${text.slice(0, 30)}…` : text;
    return snippet ? `Text: ${snippet}` : `Text ${index + 1}`;
  }
  if (obj?.type === 'image') return `Image ${index + 1}`;
  const type = obj?.type ? String(obj.type)[0].toUpperCase() + String(obj.type).slice(1) : 'Object';
  return `${type} ${index + 1}`;
}

/**
 * Renders one full-canvas-size, transparent-background PNG data URL PER Fabric
 * object on this side (text/image/shape), instead of one flattened composite —
 * so an admin's "Download as PSD" can open the file with each design object as
 * its own editable layer. Same isolate-by-toggling-`visible`-and-re-render
 * approach as the backend's own `buildPsdLayerInputsFromCanvas` (psdService.ts)
 * — see that function's doc comment for why toggling Fabric's real per-object
 * rendering is preferred over reimplementing it — applied here client-side
 * since this route (POST /api/download, used by the admin's "Download as
 * PSD" button via DesignDownloadRoot.tsx) never sends the server the raw
 * canvas JSON, only already-flattened composite images; the object isolation
 * has to happen wherever the live Fabric canvas actually exists, which for
 * this download path is only ever here, client-side.
 */
async function renderSideObjectLayerDataUrls(side: DesignSide, canvasJson: any): Promise<{ name: string; dataUrl: string }[]> {
  const { fabric } = await import('fabric');
  registerCurvedText(fabric);
  const offscreen = new fabric.StaticCanvas(undefined, {
    width:  side.canvasWidth,
    height: side.canvasHeight,
  });
  if (canvasJson) {
    await new Promise<void>(resolve => offscreen.loadFromJSON(withImageCrossOrigin(canvasJson), () => resolve()));
  }
  const objects: any[] = (offscreen as any).getObjects();
  const layers: { name: string; dataUrl: string }[] = [];
  for (let i = 0; i < objects.length; i++) {
    for (let j = 0; j < objects.length; j++) objects[j].visible = j === i;
    offscreen.renderAll();
    layers.push({ name: describeLayerName(objects[i], i), dataUrl: (offscreen as any).toDataURL({ format: 'png' }) });
  }
  offscreen.dispose();
  return layers;
}

/**
 * Same per-side iteration as buildDownloadPdfDataUrls, but produces named
 * per-object layer data URLs per side (see renderSideObjectLayerDataUrls) for
 * POST /api/download's outputType:'psd' path — sides with no design objects
 * yet resolve to an empty layer array, same as any other side with nothing
 * drawn on it.
 */
export async function buildDownloadPsdLayerDataUrls(
  sides: DesignSide[],
  canvasInstances: Map<string, FabricCanvas>,
  sideColorCountJsonCache: Record<string, Record<string, any>>,
  resolveActiveColorCountKey: (side: DesignSide) => string,
): Promise<Record<string, { name: string; dataUrl: string }[]>> {
  const result: Record<string, { name: string; dataUrl: string }[]> = {};
  for (const side of sides) {
    const canvasJson = snapshotCanvasJson(side, canvasInstances, sideColorCountJsonCache, resolveActiveColorCountKey);
    result[side.sideKey] = await renderSideObjectLayerDataUrls(side, canvasJson);
  }
  return result;
}

/**
 * Builds one full-resolution, product-photo-composited PNG data URL per side, ready to
 * send to POST /api/download with outputType:'pdf' for a one-page-per-side PDF.
 */
export async function buildDownloadPdfDataUrls(
  sides: DesignSide[],
  canvasInstances: Map<string, FabricCanvas>,
  sideColorCountJsonCache: Record<string, Record<string, any>>,
  resolveActiveColorCountKey: (side: DesignSide) => string,
): Promise<Record<string, string>> {
  const result: Record<string, string> = {};
  for (const side of sides) {
    const canvasJson = snapshotCanvasJson(side, canvasInstances, sideColorCountJsonCache, resolveActiveColorCountKey);
    const designDataUrl = await renderSideDesignDataUrl(side, canvasJson);
    result[side.sideKey] = await compositeSideForDownload(side, designDataUrl);
  }
  return result;
}

// On-screen cap for the Preview tab's composite — comfortably covers the grid tile
// (maxHeight 200) and slider (up to full viewport) at typical device pixel ratios
// without paying to decode/draw/encode a vendor's print-resolution source photo.
const PREVIEW_MAX_DIMENSION = 1000;

/**
 * Same composite as a single PDF page, for the read-only "Preview" tab — shows exactly
 * what the customer will get, instead of the editable canvas. Downscaled to
 * `PREVIEW_MAX_DIMENSION` since this only ever renders into a small tile/slider, never
 * a print output (see `compositeSideForDownload`'s `maxDimension` param).
 */
export async function buildPreviewDataUrl(
  side: DesignSide,
  canvasInstances: Map<string, FabricCanvas>,
  sideColorCountJsonCache: Record<string, Record<string, any>>,
  resolveActiveColorCountKey: (side: DesignSide) => string,
  variantOverlayUrl?: string,
): Promise<string> {
  const canvasJson = snapshotCanvasJson(side, canvasInstances, sideColorCountJsonCache, resolveActiveColorCountKey);
  const designDataUrl = await renderSideDesignDataUrl(side, canvasJson);
  return compositeSideForDownload(side, designDataUrl, variantOverlayUrl, PREVIEW_MAX_DIMENSION);
}

/**
 * Design-only PNG (transparent background, no product photo composited in) — used for
 * the template picker's thumbnail. Deliberately does NOT reuse `buildPreviewDataUrl`:
 * a template only ever carries the design objects (see `applyTemplate` in TemplateTool.tsx),
 * never the garment/product photo it happened to be created against, so a thumbnail that
 * shows that one photo baked in misleadingly suggests picking the template also changes
 * the product/color — it doesn't, and a customer picking it on a *different* color or
 * product would see no such change.
 */
export async function buildTemplateThumbnailDataUrl(
  side: DesignSide,
  canvasInstances: Map<string, FabricCanvas>,
  sideColorCountJsonCache: Record<string, Record<string, any>>,
  resolveActiveColorCountKey: (side: DesignSide) => string,
): Promise<string> {
  const canvasJson = snapshotCanvasJson(side, canvasInstances, sideColorCountJsonCache, resolveActiveColorCountKey);
  return renderSideDesignDataUrl(side, canvasJson);
}
